import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type {
  AdminUsersSliceState,
  GetAdminUsersResponse,
} from '@/domains/adminUsers/store/types/AdminUsersSliceState';
import type { UpdateUserRolePayload, UpdateUserStatusPayload } from '@/domains/adminUsers/types/AdminUserInformation';
import { EDSFiltersRequestBody } from '@/features/eds/types/api/requests/EDSFiltersRequestBody';
import { EdsSortingRequest } from '@/features/eds/types/api/requests/EdsSortingRequestBody';

const initialState: AdminUsersSliceState = {
  data: {
    listings: [],
    pagination: {
      numberOfItems: 0,
      first: '1',
      last: undefined,
      previous: '0',
      next: '0',
      page: 1,
      perPage: 50,
    },
  },
  statuses: {
    isLoading: false,
    isError: false,
  },
  searchQuery: '',
  savedFiltersForSending: null,
  savedSortingForSending: null,
};

export const adminUsersSlice = createSlice({
  name: 'adminUsersSlice',
  initialState,
  reducers: {
    getAdminUsers: () => {},
    getAdminUsersSuccess: (
      state,
      {
        payload: { shouldRewrite = true, data },
      }: PayloadAction<{ shouldRewrite?: boolean; data: GetAdminUsersResponse }>,
    ) => {
      const listingsArray = Array.isArray(data.listings) ? data.listings : [data.listings];

      if (shouldRewrite) {
        state.data.listings = [...listingsArray];
        state.data.pagination = data.pagination;
      } else {
        state.data.listings = [...state.data.listings, ...listingsArray];
        state.data.pagination = data.pagination;
      }

      state.statuses.isLoading = false;
    },
    incrementAdminUsersPage: (state) => {
      state.data.pagination.page++;
      state.statuses.isLoading = true;
    },
    setLoadingAdminUsersData: (state, { payload }: PayloadAction<boolean>) => {
      state.statuses.isLoading = payload;
    },
    getAdminUsersDataError: (state) => {
      state.statuses.isLoading = false;
      state.statuses.isError = true;
    },
    resetAdminUserList(state) {
      state.data.pagination = initialState.data.pagination;
      state.data.listings = initialState.data.listings;
    },
    updateAdminUserStatus: (_, _action: PayloadAction<UpdateUserStatusPayload>) => {},
    updateAdminUserStatusSuccess: (state, { payload: { id, status } }: PayloadAction<UpdateUserStatusPayload>) => {
      const userIndex = state.data.listings.findIndex((user) => user.id === id);
      if (userIndex !== -1) {
        const user = state.data.listings[userIndex];
        if (!user.fields.accountStatusId) {
          return;
        }
        const statusId = user.fields.accountStatusId.validation?.allowedValues?.find(
          ({ label }) => label === status,
        )?.value;

        if (!statusId && statusId !== 0) {
          return;
        }
        user.fields.accountStatusId.value = statusId;
      }
    },
    updateAdminUserRole: (_, _action: PayloadAction<UpdateUserRolePayload>) => {},
    updateAdminUserRoleSuccess: (state, { payload }: PayloadAction<UpdateUserRolePayload>) => {
      const userIndex = state.data.listings.findIndex((user) => user.id === payload.id);

      if (userIndex !== -1) {
        const user = state.data.listings[userIndex];

        if (user.fields.roles) {
          user.fields.roles.value =
            user.fields.roles.validation?.allowedValues
              ?.filter(({ label }) => payload.userRoles.includes(label))
              .map(({ value }) => value)
              .join(',') || '';
        }
      }
    },
    searchAdminUsers: (state, { payload }: PayloadAction<string>) => {
      state.data.pagination.page = 1;
      state.searchQuery = payload;
      state.data.listings = [];
    },
    updateAdminUsersSavedFiltersForSending: (state, { payload }: PayloadAction<EDSFiltersRequestBody>) => {
      state.savedFiltersForSending = payload;
    },
    updateAdminUsersSavedSortingForSending: (state, { payload }: PayloadAction<EdsSortingRequest>) => {
      state.savedSortingForSending = payload;
    },
  },
});

export const {
  updateAdminUserStatus,
  updateAdminUserStatusSuccess,
  updateAdminUserRole,
  updateAdminUserRoleSuccess,
  setLoadingAdminUsersData,
  getAdminUsers,
  getAdminUsersSuccess,
  incrementAdminUsersPage,
  getAdminUsersDataError,
  searchAdminUsers,
  updateAdminUsersSavedFiltersForSending,
  updateAdminUsersSavedSortingForSending,
} = adminUsersSlice.actions;

export const adminUsersSliceReducer = adminUsersSlice.reducer;
export const adminUsersSliceReducerPath = adminUsersSlice.reducerPath;
